/**
 * regular-width-fluid / regular-width-1680 版心内 Swiper 轮播对齐工具
 * - 轨道 breakout（--fluid-gutter-inline）后，offset 相对轨道左缘计算
 * - --rw-carousel-content-w：版心内容区实测宽度（PC slide 宽度）
 * - --rw-carousel-gap：与 Swiper spaceBetween 同步的流体间距（px）
 * - --rw-mobile-slide-1：标准卡宽 = contentW - gap - peek（下一张固定露出 peek）
 * - --rw-mobile-slide-2：半宽卡 = slide-1 / 2（2 张半宽 + peek = 版心宽）
 */
(function () {
  if (window.RegularWidthCarousel) return;

  var MOBILE_PEEK_PX = 30;
  var GUARD_STYLE_ID = 'rw-carousel-guard-styles';

  function injectGuardStyles() {
    if (document.getElementById(GUARD_STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = GUARD_STYLE_ID;
    style.textContent = [
      '.swiper img,',
      '.swiper video,',
      '.swiper picture,',
      '.swiper source {',
      '  -webkit-user-drag: none;',
      '  user-drag: none;',
      '  -webkit-user-select: none;',
      '  user-select: none;',
      '}',
      '.swiper .swiper-slide img,',
      '.swiper .swiper-slide video,',
      '.swiper .swiper-slide picture {',
      '  pointer-events: none;',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }

  function guardSwiperMedia(root) {
    if (!root || !root.querySelectorAll) return;

    root.querySelectorAll('img, video, picture').forEach(function (el) {
      el.setAttribute('draggable', 'false');
    });
  }

  function isSwiperDragTarget(target) {
    if (!target || !target.closest) return false;
    if (!target.closest('.swiper')) return false;

    var tag = target.tagName;
    return (
      tag === 'IMG' ||
      tag === 'VIDEO' ||
      tag === 'PICTURE' ||
      tag === 'A' ||
      target.closest('a')
    );
  }

  document.addEventListener(
    'dragstart',
    function (event) {
      if (isSwiperDragTarget(event.target)) {
        event.preventDefault();
      }
    },
    true
  );

  function syncFluidGutterFromPadding(container) {
    if (!container) return 0;

    const style = window.getComputedStyle(container);
    const padLeft = parseFloat(style.paddingLeft) || 0;
    const padRight = parseFloat(style.paddingRight) || 0;

    if (Math.abs(padLeft - padRight) < 1) {
      container.style.setProperty('--fluid-gutter-inline', padLeft + 'px');
    }

    return padLeft;
  }

  /** 设计稿 px × 当前连续 --rem（PC: clientWidth/1920；移动: 1） */
  function getFluidPx(designPx) {
    const rem = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--rem')) || 1;
    return (Number(designPx) || 0) * rem;
  }

  function applyCarouselGap(container, designGapPx) {
    if (!container || designGapPx === undefined || designGapPx === null) {
      return getFluidPx(designGapPx || 0);
    }

    const gap = getFluidPx(designGapPx);
    container.style.setProperty('--rw-carousel-gap', gap + 'px');
    return gap;
  }

  function getMetrics(container, track) {
    if (!container) {
      return { before: 0, after: 0, contentWidth: 0, contentLeft: 0, contentRight: 0 };
    }

    syncFluidGutterFromPadding(container);

    const style = window.getComputedStyle(container);
    const padLeft = parseFloat(style.paddingLeft) || 0;
    const padRight = parseFloat(style.paddingRight) || 0;
    const containerRect = container.getBoundingClientRect();
    const contentLeft = containerRect.left + padLeft;
    const contentRight = containerRect.right - padRight;
    const contentWidth = Math.max(0, contentRight - contentLeft);

    let before = contentLeft;
    let after = Math.max(0, window.innerWidth - contentRight);

    if (track) {
      const trackRect = track.getBoundingClientRect();
      before = contentLeft - trackRect.left;
      after = trackRect.right - contentRight;
    }

    return {
      before: Math.max(0, before),
      after: Math.max(0, after),
      contentWidth: contentWidth,
      contentLeft: contentLeft,
      contentRight: contentRight,
    };
  }

  function applyContentWidth(container, metrics) {
    if (!container || !metrics) return;
    container.style.setProperty('--rw-carousel-content-w', metrics.contentWidth + 'px');
  }

  function getMobilePeek(container) {
    if (container && container.dataset.mobilePeek !== undefined && container.dataset.mobilePeek !== '') {
      return Math.max(0, Number(container.dataset.mobilePeek) || 0);
    }
    return MOBILE_PEEK_PX;
  }

  function applyMobileSlideWidths(container, gap, metrics) {
    if (!container) return;
    const m = metrics || getMetrics(container, null);
    const g = Number(gap) || 0;
    const peek = getMobilePeek(container);
    const slide1 = Math.max(0, m.contentWidth - g - peek);
    const slide2 = slide1 / 2;
    container.style.setProperty('--rw-mobile-slide-1', slide1 + 'px');
    container.style.setProperty('--rw-mobile-slide-2', slide2 + 'px');
    container.style.setProperty('--rw-mobile-peek', peek + 'px');
  }

  function getSwiperOffsets(container, track, designGapPx) {
    const metrics = getMetrics(container, track);
    applyContentWidth(container, metrics);
    let gapPx = null;
    if (designGapPx !== undefined && designGapPx !== null) {
      gapPx = applyCarouselGap(container, designGapPx);
      applyMobileSlideWidths(container, gapPx, metrics);
    }
    return {
      before: metrics.before,
      after: metrics.after,
      contentWidth: metrics.contentWidth,
      gap: gapPx,
    };
  }

  /**
   * 轮播不 breakout、轨道在版心 padding 内时：
   * Swiper 宽度已是内容区，slidesOffset 应为 0；仅同步 --rw-carousel-content-w 供 slide 宽度计算
   */
  function getContainedSwiperOffsets(container, designGapPx) {
    const metrics = getMetrics(container, null);
    applyContentWidth(container, metrics);
    let gapPx = null;
    if (designGapPx !== undefined && designGapPx !== null) {
      gapPx = applyCarouselGap(container, designGapPx);
      applyMobileSlideWidths(container, gapPx, metrics);
    }
    return {
      before: 0,
      after: 0,
      contentWidth: metrics.contentWidth,
      gap: gapPx,
    };
  }

  function getSlidesPerView(swiper) {
    if (!swiper) return 1;

    var perView = 1;
    if (typeof swiper.slidesPerViewDynamic === 'function') {
      perView = swiper.slidesPerViewDynamic();
    }
    if (!perView || perView < 1) {
      perView = 1;
    }
    return perView;
  }

  function refreshClippedSlideMetrics(swiper) {
    if (!swiper || swiper.destroyed) return;
    if (typeof swiper.updateSize === 'function') swiper.updateSize();
    if (typeof swiper.updateSlides === 'function') swiper.updateSlides();
  }

  /** 每张卡片左侧在轨道内的滚动偏移（px） */
  function getSlideScrollOffsets(swiper) {
    if (!swiper || !swiper.slides || !swiper.slides.length) {
      return [0];
    }

    refreshClippedSlideMetrics(swiper);

    var space = Number(swiper.params.spaceBetween) || 0;
    var offsets = [0];
    var total = 0;

    for (var i = 1; i < swiper.slides.length; i += 1) {
      var prevSize = swiper.slides[i - 1].swiperSlideSize;
      if (!prevSize || prevSize <= 0) {
        prevSize = swiper.slides[i - 1].offsetWidth;
      }
      total += prevSize + space;
      offsets.push(total);
    }

    return offsets;
  }

  /**
   * 纯计算分页停靠位，不移动 Swiper（兼容宽/窄混排）
   * 返回 [{ slideIndex, translate }, ...]
   */
  function buildClippedPages(swiper) {
    if (!swiper || swiper.destroyed || !swiper.slides || !swiper.slides.length) {
      return [{ slideIndex: 0, translate: 0 }];
    }

    if (swiper.isLocked) {
      return [{ slideIndex: swiper.activeIndex || 0, translate: swiper.getTranslate() }];
    }

    refreshClippedSlideMetrics(swiper);

    var offsets = getSlideScrollOffsets(swiper);
    var maxTranslate = swiper.maxTranslate();
    var maxScroll = Math.abs(maxTranslate);
    var pages = [];
    var seen = {};

    for (var i = 0; i < offsets.length; i += 1) {
      if (offsets[i] > maxScroll + 1) {
        break;
      }

      var translate = Math.max(maxTranslate, -offsets[i]);
      var key = translate.toFixed(2);
      if (seen[key]) {
        continue;
      }

      seen[key] = true;
      pages.push({
        slideIndex: i,
        translate: translate,
      });
    }

    if (maxScroll > 0) {
      var endKey = maxTranslate.toFixed(2);
      var lastPage = pages[pages.length - 1];
      if (!seen[endKey] && (!lastPage || Math.abs(lastPage.translate - maxTranslate) > 1)) {
        pages.push({
          slideIndex: swiper.slides.length - 1,
          translate: maxTranslate,
        });
      }
    }

    if (pages.length <= 1 && swiper.slides.length > 1) {
      var perView = Math.ceil(getSlidesPerView(swiper));
      if (swiper.slides.length > perView) {
        pages = [];
        var fallbackCount = swiper.slides.length - perView + 1;
        for (var j = 0; j < fallbackCount; j += 1) {
          var fallbackTranslate = offsets[j] !== undefined
            ? Math.max(maxTranslate, -offsets[j])
            : maxTranslate;
          pages.push({
            slideIndex: j,
            translate: fallbackTranslate,
          });
        }
        if (maxScroll > 0) {
          var lastFallback = pages[pages.length - 1];
          if (!lastFallback || Math.abs(lastFallback.translate - maxTranslate) > 1) {
            pages.push({
              slideIndex: swiper.slides.length - 1,
              translate: maxTranslate,
            });
          }
        }
      }
    }

    return pages.length ? pages : [{ slideIndex: 0, translate: 0 }];
  }

  function getClippedPageCount(swiper, pages) {
    var list = pages || buildClippedPages(swiper);
    return list.length;
  }

  function getCurrentClippedPageIndex(pages, swiper) {
    if (!pages || !pages.length || !swiper) return 0;

    var current = swiper.getTranslate();
    var best = 0;
    var bestDistance = Infinity;

    pages.forEach(function (page, index) {
      var distance = Math.abs(current - page.translate);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });

    return best;
  }

  function goToClippedPage(swiper, pageIndex, pages) {
    if (!swiper || swiper.destroyed || !pages || !pages[pageIndex]) return;

    var page = pages[pageIndex];
    var speed = typeof swiper.params.speed === 'number' ? swiper.params.speed : 300;

    if (typeof swiper.translateTo === 'function') {
      swiper.translateTo(page.translate, speed, true);
      return;
    }

    swiper.slideTo(page.slideIndex, speed);
  }

  function getClippedNavState(swiper) {
    if (!swiper || !swiper.slides || !swiper.slides.length) {
      return { pageCount: 0, atStart: true, atEnd: true };
    }

    return {
      pageCount: getClippedPageCount(swiper),
      atStart: !!swiper.isBeginning,
      atEnd: !!swiper.isEnd,
    };
  }

  function setupClippedPagination(swiper, paginationEl) {
    if (!swiper || !paginationEl) {
      return function () {};
    }

    var bullets = [];
    var pages = [];
    var onSlideChange = null;
    var onResize = null;

    function syncPaginationVisibility(pageCount) {
      paginationEl.style.setProperty(
        'display',
        pageCount > 1 ? 'flex' : 'none',
        'important'
      );
    }

    function updateBullets() {
      if (!bullets.length || !pages.length) {
        return;
      }

      var pageIndex = getCurrentClippedPageIndex(pages, swiper);
      bullets.forEach(function (bullet, index) {
        bullet.classList.toggle('swiper-pagination-bullet-active', index === pageIndex);
      });
    }

    function renderBullets() {
      paginationEl.innerHTML = '';
      bullets = [];
      pages = buildClippedPages(swiper);

      if (!swiper.slides || !swiper.slides.length) {
        syncPaginationVisibility(0);
        return;
      }

      var pageCount = pages.length;
      syncPaginationVisibility(pageCount);

      if (pageCount <= 1) {
        return;
      }

      pages.forEach(function (page, pageIndex) {
        var bullet = document.createElement('span');
        bullet.className = 'swiper-pagination-bullet';
        bullet.setAttribute('role', 'button');
        bullet.setAttribute('tabindex', '0');
        bullet.setAttribute('aria-label', 'Go to page ' + (pageIndex + 1));
        bullet.addEventListener('click', function () {
          if (swiper.destroyed) return;
          goToClippedPage(swiper, pageIndex, pages);
        });
        bullet.addEventListener('keydown', function (event) {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          if (swiper.destroyed) return;
          goToClippedPage(swiper, pageIndex, pages);
        });
        paginationEl.appendChild(bullet);
        bullets.push(bullet);
      });

      updateBullets();
    }

    onSlideChange = function () {
      updateBullets();
    };

    onResize = function () {
      renderBullets();
    };

    renderBullets();
    swiper.on('slideChange', onSlideChange);
    swiper.on('slideChangeTransitionEnd', onSlideChange);
    swiper.on('resize', onResize);

    return function cleanup() {
      if (swiper && swiper.off) {
        swiper.off('slideChange', onSlideChange);
        swiper.off('slideChangeTransitionEnd', onSlideChange);
        swiper.off('resize', onResize);
      }
      paginationEl.innerHTML = '';
      paginationEl.style.removeProperty('display');
      bullets = [];
      pages = [];
    };
  }

  var NO_SWIPING_SELECTOR =
    'button, input, select, textarea, .swiper-no-swiping';

  function getInteractionDefaults(overrides) {
    var defaults = {
      grabCursor: true,
      simulateTouch: true,
      touchStartPreventDefault: false,
      passiveListeners: true,
      watchOverflow: false,
      threshold: 5,
      shortSwipes: true,
      longSwipes: true,
      followFinger: true,
      touchRatio: 1,
      resistanceRatio: 0.85,
      noSwipingSelector: NO_SWIPING_SELECTOR,
      mousewheel: {
        enabled: true,
        forceToAxis: true,
        sensitivity: 1,
        thresholdDelta: 4,
        releaseOnEdges: false,
      },
    };

    if (!overrides) return defaults;

    var result = Object.assign({}, defaults, overrides);
    if (overrides.mousewheel) {
      result.mousewheel = Object.assign({}, defaults.mousewheel, overrides.mousewheel);
    }
    return result;
  }

  function mergeSwiperOptions(custom) {
    return getInteractionDefaults(custom);
  }

  function refreshSwiperLayout(swiper, container, track, designGapPx) {
    if (!swiper || swiper.destroyed || !container) return null;

    const offsets =
      track && track.getBoundingClientRect
        ? getSwiperOffsets(container, track, designGapPx)
        : getContainedSwiperOffsets(container, designGapPx);

    swiper.params.slidesOffsetBefore = offsets.before;
    swiper.params.slidesOffsetAfter = offsets.after;
    if (offsets.gap !== null && offsets.gap !== undefined) {
      swiper.params.spaceBetween = offsets.gap;
    }

    if (typeof swiper.updateSize === 'function') swiper.updateSize();
    if (typeof swiper.updateSlides === 'function') swiper.updateSlides();
    if (typeof swiper.update === 'function') swiper.update();

    return offsets;
  }

  function observeContainer(container, callback) {
    if (!container || typeof callback !== 'function') {
      return function () {};
    }

    if (typeof ResizeObserver === 'undefined') {
      return function () {};
    }

    let frame = null;
    const observer = new ResizeObserver(function () {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(function () {
        frame = null;
        callback();
      });
    });

    observer.observe(container);
    syncFluidGutterFromPadding(container);

    return function cleanup() {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }

  function primeContainer(container) {
    if (!container) return;
    syncFluidGutterFromPadding(container);
    applyContentWidth(container, getMetrics(container, null));
  }

  function createSwiper(el, options) {
    if (!el || typeof Swiper === 'undefined') return null;

    guardSwiperMedia(el);
    var instance = new Swiper(el, mergeSwiperOptions(options));
    guardSwiperMedia(el);

    requestAnimationFrame(function () {
      if (!instance.destroyed) {
        if (typeof instance.updateSize === 'function') instance.updateSize();
        if (typeof instance.update === 'function') instance.update();
      }
    });

    return instance;
  }

  function getMobileSwiperOptions(container, track, designGapPx) {
    const offsets = getSwiperOffsets(container, track, designGapPx);
    return getInteractionDefaults({
      slidesPerView: 'auto',
      spaceBetween: offsets.gap != null ? offsets.gap : getFluidPx(designGapPx || 0),
      slidesOffsetBefore: offsets.before,
      slidesOffsetAfter: offsets.after,
    });
  }

  injectGuardStyles();

  window.RegularWidthCarousel = {
    MOBILE_PEEK_PX: MOBILE_PEEK_PX,
    NO_SWIPING_SELECTOR: NO_SWIPING_SELECTOR,
    syncFluidGutterFromPadding: syncFluidGutterFromPadding,
    getFluidPx: getFluidPx,
    applyCarouselGap: applyCarouselGap,
    primeContainer: primeContainer,
    getMetrics: getMetrics,
    getMobilePeek: getMobilePeek,
    applyContentWidth: applyContentWidth,
    applyMobileSlideWidths: applyMobileSlideWidths,
    getSwiperOffsets: getSwiperOffsets,
    getContainedSwiperOffsets: getContainedSwiperOffsets,
    refreshSwiperLayout: refreshSwiperLayout,
    observeContainer: observeContainer,
    getSlidesPerView: getSlidesPerView,
    buildClippedPages: buildClippedPages,
    getClippedPageCount: getClippedPageCount,
    getCurrentClippedPageIndex: getCurrentClippedPageIndex,
    goToClippedPage: goToClippedPage,
    getClippedNavState: getClippedNavState,
    setupClippedPagination: setupClippedPagination,
    getInteractionDefaults: getInteractionDefaults,
    mergeSwiperOptions: mergeSwiperOptions,
    getMobileSwiperOptions: getMobileSwiperOptions,
    guardSwiperMedia: guardSwiperMedia,
    createSwiper: createSwiper,
  };
})();
